import utcToZonedTime from "date-fns-tz/utcToZonedTime"
import addDays from "date-fns/addDays"
import eachMinuteOfInterval from "date-fns/eachMinuteOfInterval"
import format from "date-fns/format"
import isAfter from "date-fns/isAfter"
import parseISO from "date-fns/parseISO"
import setHours from "date-fns/setHours"
import setMinutes from "date-fns/setMinutes"
import PropTypes from "prop-types"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useMutation, useQuery, useQueryClient } from "react-query"

import AlertBanner from "src/components/AlertBanner"
import Form from "src/components/Form"
import Modal from "src/components/Modal"

import {
  createBoaterScheduledLaunch,
  queryBoaterBookableScheduleAvailability,
  queryBoaterLaunchAddOnEstimate,
  queryBoaterLaunchAddOns,
} from "src/api/DryStack"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { availableTime } from "src/utils/form/available_time"
import { titlecase } from "src/utils/string_helpers"

import LaunchAddOnFields from "../AddOns/LaunchAddOnFields"
import {
  hasIncompleteAddOnSelection,
  isSubmittable,
} from "../AddOns/launchAddOnPricing"
import { useLaunchAddOnEstimate } from "../AddOns/useLaunchAddOnEstimate"
import { validateFutureTime } from "../MarinaSchedule/MarinaScheduleSharedMethods"

const ScheduleLaunchModal = ({
  closeModal,
  launchableReservations,
  isOpen,
}) => {
  const queryClient = useQueryClient()
  const showToast = useToast()
  const tracker = useTracker()

  const [selectedReservation, setSelectedReservation] = useState(null)
  const [maxDate, setMaxDate] = useState(new Date())

  const addOnsSectionRef = useRef(null)

  const { data: addOnCatalog, isError: addOnCatalogError } = useQuery(
    ["boaterLaunchAddOns", selectedReservation?.encodedId],
    () => queryBoaterLaunchAddOns(selectedReservation.encodedId),
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: Boolean(selectedReservation),
    }
  )

  const addOnCatalogItems = useMemo(
    () =>
      (addOnCatalog ?? []).map((product) => ({
        productId: product.id,
        name: product.name,
        productType: product.productType,
        fuel: product.fuel,
        pricePerUnit: product.pricePerUnit,
        pricePerUnitFormatted: product.pricePerUnitFormatted,
        pricePrecision: product.pricePrecision,
        outOfStock: product.outOfStock,
      })),
    [addOnCatalog]
  )

  const {
    register,
    control,
    handleSubmit,
    getValues,
    reset,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      estimatedReturn: null,
      date: new Date(),
      location: "",
      note: "",
      time: new Date(),
      reservation: null,
      addOns: [],
    },
  })
  const selectedDate = watch("date")
  const addOns = watch("addOns")

  const addOnEstimate = useLaunchAddOnEstimate({
    catalog: addOnCatalogItems,
    value: addOns,
    enabled: Boolean(selectedReservation),
    scopeId: selectedReservation?.encodedId,
    fetchEstimate: (selection) =>
      queryBoaterLaunchAddOnEstimate(selectedReservation.encodedId, selection),
  })

  const createMutation = useMutation(
    (formState) => createBoaterScheduledLaunch(formState),
    {
      onSuccess: () => {
        showToast("New launch created", { type: "success" })
        handleSuccess()
      },
    }
  )

  const {
    data: bookableScheduleAvailabilityData,
    isFetching: fetchingAvailability,
  } = useQuery(
    [
      "bookableScheduleAvailability",
      format(selectedDate, "yyyy-MM-dd"),
      selectedReservation?.marinaId,
    ].filter(Boolean),
    () =>
      queryBoaterBookableScheduleAvailability(
        format(selectedDate, "yyyy-MM-dd"),
        selectedReservation?.marinaId
      ),
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: Boolean(selectedReservation),
      onSuccess: (data) => {
        if (!data) return

        if (data.availableLocations.length > 0) {
          setValue("location", data.availableLocations[0])
          setValue("time", getDefaultTimeValue(data))
        } else {
          setValue("location", "None")
          setValue("time", "None")
        }

        setMaxDate(calculateMaxDate())
      },
    }
  )

  const getDefaultTimeValue = useCallback(
    (availabilityData) => {
      const data = availabilityData || bookableScheduleAvailabilityData
      if (!data?.availableTimes?.length) {
        return setHours(setMinutes(selectedDate, 0), 8)
      }

      const availableTimes = data.availableTimes
      const marinaTimezone = selectedReservation?.marinaTimezone

      // On today's date, default to the next future available time
      if (marinaTimezone) {
        const currentMarinaTime = utcToZonedTime(new Date(), marinaTimezone)
        const isToday =
          format(selectedDate, "yyyy-MM-dd") ===
          format(currentMarinaTime, "yyyy-MM-dd")

        if (isToday) {
          const currentTimeString = format(currentMarinaTime, "HH:mm")
          const futureTime = availableTimes.find(
            (time) => time > currentTimeString
          )
          if (futureTime) {
            const [hours, minutes] = futureTime.split(":").map(Number)
            return setHours(setMinutes(selectedDate, minutes), hours)
          }
        }
      }

      const [hours, minutes] = availableTimes[0].split(":").map(Number)
      return setHours(setMinutes(selectedDate, minutes), hours)
    },
    [bookableScheduleAvailabilityData, selectedDate, selectedReservation]
  )

  const calculateMaxDate = useCallback(() => {
    if (!bookableScheduleAvailabilityData) return

    const reservationCheckOutDate = parseISO(selectedReservation.checkOutDate)

    const maximumLeadTimeDays =
      bookableScheduleAvailabilityData.maximumLeadTimeDays
    const today = new Date()

    if (maximumLeadTimeDays) {
      const maximumLeadTimeDate = addDays(today, maximumLeadTimeDays)

      if (isAfter(reservationCheckOutDate, maximumLeadTimeDate)) {
        return maximumLeadTimeDate
      }
    }

    return reservationCheckOutDate
  }, [bookableScheduleAvailabilityData, selectedReservation])

  useEffect(() => {
    if (launchableReservations?.length > 0) {
      setValue("reservation", launchableReservations[0].encodedId)
      setSelectedReservation(launchableReservations[0])
    }
  }, [launchableReservations, setValue])

  // A missing, unpriced, out-of-stock, or fuel-incomplete selection all roll
  // the entire launch back server-side (ScheduleAppointment.transaction),
  // telling the boater only that the item is unavailable — the addOns
  // Controller's validate rule stops that reaching submit at all. This only
  // has to handle getting the boater's eyes to the reason: the Add-Ons
  // section sits above Notes and Estimated Return in a scrolling modal body,
  // so the error is otherwise off-screen.
  const handleInvalidSubmit = (formErrors) => {
    if (formErrors.addOns) {
      addOnsSectionRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "center",
      })
    }
  }

  const handleFormSubmit = (formData) => {
    const data = {
      reservation_id: formData.reservation,
      marina_id: selectedReservation?.marinaId,
      note: formData.note,
      start_time: format(formData.time, "HH:mm"),
      start_date: format(formData.date, "yyyy-MM-dd"),
      location: formData.location,
      estimated_return_time: formData.estimatedReturn
        ? format(
            utcToZonedTime(
              formData.estimatedReturn,
              selectedReservation.marinaTimezone
            ),
            "yyyy-MM-dd HH:mm"
          )
        : null,
      // The addOns Controller's validate rule already blocks submission on any
      // non-submittable selection, but filtering the payload through the same
      // predicate means this can't silently drift out of sync with that guard
      // later.
      addons: formData.addOns
        .filter((addOn) =>
          isSubmittable(
            addOnCatalogItems.find(
              (item) => item.productId === addOn.productId
            ),
            addOn
          )
        )
        .map((addOn) => ({
          product_id: addOn.productId,
          quantity: addOn.quantity,
          fuel_mode: addOn.fuelMode,
        })),
    }

    tracker.trackEvent("dry_stack_boater_view:new_launch_saved", {
      marina_id: selectedReservation.marinaMcomId,
      marina_name: selectedReservation.marinaName,
    })

    if (data.note) {
      tracker.trackEvent("dry_stack_boater_view:new_launch_note_added", {
        marina_id: selectedReservation.marinaMcomId,
        marina_name: selectedReservation.marinaName,
      })
    }

    createMutation.mutate(data)
  }

  // The catalog is per-marina and launchableReservations can span marinas, so a
  // selection made against the old reservation may not exist under the new one.
  // Left in state it renders nowhere (the new catalog has no row for it) but is
  // still submitted, and Replace then fails the whole launch over an item the
  // boater cannot see.
  const handleReservationChange = (encodedId) => {
    const reservation = launchableReservations.find(
      (candidate) => candidate.encodedId === encodedId
    )

    if (reservation?.encodedId !== selectedReservation?.encodedId) {
      setValue("addOns", [])
      clearErrors("addOns")
    }

    setSelectedReservation(reservation)
  }

  const handleClose = () => {
    createMutation.reset()
    queryClient.invalidateQueries("bookableScheduleAvailability")
    setSelectedReservation(launchableReservations[0])
    reset({
      date: new Date(),
      time: getDefaultTimeValue(),
      reservation: launchableReservations[0]?.encodedId,
      location: bookableScheduleAvailabilityData?.availableLocations[0],
      note: "",
      addOns: [],
      estimatedReturn: null,
    })
    closeModal()
  }

  const handleSuccess = () => {
    queryClient.invalidateQueries("launches")
    handleClose()
  }

  const hasNoAvailability = () => {
    if (!bookableScheduleAvailabilityData) return false

    return (
      bookableScheduleAvailabilityData.availableTimes.length === 0 ||
      bookableScheduleAvailabilityData.availableLocations.length === 0
    )
  }

  const generateTimeOptions = () => {
    const formattedDate = format(selectedDate, "yyyy-MM-dd")
    const operatingHours = bookableScheduleAvailabilityData.operatingHours
    const options = []

    if (operatingHours && operatingHours.length > 0) {
      operatingHours.forEach((range) => {
        const [startTime, endTime] = range
        const start = new Date(`${formattedDate} ${startTime}`)
        const end = new Date(`${formattedDate} ${endTime}`)

        const timeIntervals = eachMinuteOfInterval(
          { start, end },
          { step: bookableScheduleAvailabilityData.eventDuration }
        )

        options.push(...timeIntervals)
      })
    }

    return options
  }

  const renderLocationOptions = () => {
    return bookableScheduleAvailabilityData.availableLocations.map(
      (location, index) => {
        return (
          <Form.Select.RichOption key={index} value={location} id={index}>
            {titlecase(location)}
          </Form.Select.RichOption>
        )
      }
    )
  }

  const renderAvailabilityInputs = () => {
    if (fetchingAvailability) {
      return (
        <div className="col-span-12 flex items-center justify-center md:col-span-8">
          <div className="mt-6">
            <i className="fa-solid fa-spinner-third fa-spin mr-3" />
            <span>Loading availability</span>
          </div>
        </div>
      )
    } else {
      const shouldDisableInputs = hasNoAvailability()

      return (
        <div className="col-span-12 grid grid-cols-2 gap-4 md:col-span-8">
          <div className="col-span-2 md:col-span-1">
            <Form.Label htmlFor="schedule-appointment-time">Time</Form.Label>
            <Controller
              name={"time"}
              control={control}
              rules={{
                validate: (value) => {
                  if (!bookableScheduleAvailabilityData) {
                    return "Time must be one of the selectable values"
                  }
                  if (
                    !validateFutureTime(
                      value,
                      getValues("date"),
                      selectedReservation.marinaTimezone
                    )
                  ) {
                    return "Time must be in the future"
                  }
                  return true
                },
              }}
              render={({ field: { onChange, value } }) => {
                if (bookableScheduleAvailabilityData?.availableTimes?.length) {
                  return (
                    <Form.TimePicker
                      id="schedule-appointment-time"
                      hasErrors={!!errors?.time}
                      disabled={shouldDisableInputs}
                      filterTime={(time) =>
                        availableTime(
                          time,
                          bookableScheduleAvailabilityData.availableTimes
                        )
                      }
                      injectTimes={generateTimeOptions()}
                      onFocus={(event) => (event.target.readOnly = true)}
                      {...{ onChange, value }}
                    />
                  )
                } else {
                  return <Form.TextField value="None" disabled />
                }
              }}
            />
            {errors?.time && <Form.Error>{errors.time.message}</Form.Error>}
          </div>
          <div className="col-span-2 md:col-span-1">
            <Form.Label htmlFor="schedule-appointment-location">
              Launch Location
            </Form.Label>
            <Controller
              control={control}
              name="location"
              render={({ field: { onChange, value } }) => {
                if (bookableScheduleAvailabilityData?.availableTimes?.length) {
                  return (
                    <Form.Select.Custom
                      id="schedule-appointment-location"
                      onChange={onChange}
                      disabled={shouldDisableInputs}
                      value={value}
                    >
                      {renderLocationOptions()}
                    </Form.Select.Custom>
                  )
                } else {
                  return <Form.TextField value="None" disabled />
                }
              }}
            />
          </div>
        </div>
      )
    }
  }

  const renderExceptionNote = () => {
    if (fetchingAvailability) return null

    const exceptionNote = bookableScheduleAvailabilityData?.exceptionNote

    if (exceptionNote) {
      return (
        <div className="col-span-12">
          <AlertBanner type={hasNoAvailability() ? "error" : "info"}>
            {hasNoAvailability() ? "No availability on this date: " : null}
            {exceptionNote}
          </AlertBanner>
        </div>
      )
    } else if (hasNoAvailability()) {
      return (
        <div className="col-span-12">
          <AlertBanner type="error">No availability on this date</AlertBanner>
        </div>
      )
    }
  }

  const renderReservationSelect = () => {
    return (
      <Controller
        control={control}
        name="reservation"
        render={({ field: { onChange, onBlur, value } }) => (
          <Form.Select.Custom
            id="schedule-appointment-reservation"
            onChange={(value) => {
              onChange(value)
              handleReservationChange(value)
            }}
            onBlur={onBlur}
            value={value}
          >
            {launchableReservations.map((reservation, index) => {
              return (
                <Form.Select.RichOption
                  key={reservation.encodedId}
                  value={reservation.encodedId}
                  id={reservation.encodedId}
                >
                  {reservation.marinaName} - {reservation.contactBoat.name}
                </Form.Select.RichOption>
              )
            })}
          </Form.Select.Custom>
        )}
      />
    )
  }

  const renderDateInput = () => (
    <div className="col-span-12 md:col-span-4">
      <Form.Label htmlFor="schedule-appointment-date">Date</Form.Label>
      <Controller
        name={"date"}
        control={control}
        render={({ field: { onChange, value } }) => (
          <Form.DatePicker
            id="schedule-appointment-date"
            {...{ onChange, value }}
            minDate={new Date()}
            maxDate={maxDate}
            onFocus={(event) => (event.target.readOnly = true)}
          />
        )}
      />
    </div>
  )

  const renderNoteInput = () => (
    <div className="col-span-12">
      <Form.Label htmlFor="schedule-appointment-note">Notes</Form.Label>
      <Form.Textarea
        {...register("note")}
        placeholder="Add notes"
        id="schedule-appointment-note"
      />
      <Form.Subtext>
        Use notes to add additional information about this launch (i.e.
        estimated return time, special requests, etc.)
      </Form.Subtext>
    </div>
  )

  const renderEstimatedReturnInput = () => (
    <div className="col-span-12 pb-6">
      <Form.Label htmlFor="estimated-return" optional>
        Estimated Return
      </Form.Label>
      <Controller
        name={"estimatedReturn"}
        control={control}
        rules={{
          validate: (value, formData) => {
            if (
              value &&
              !validateFutureTime(
                value,
                value,
                selectedReservation.marinaTimezone
              )
            ) {
              return "Estimated return must be in the future"
            }
            if (value && value < formData.time) {
              return "Estimated return must be after start time"
            }
            return true
          },
        }}
        render={({ field: { onChange, value } }) => (
          <Form.DatePicker
            id="estimated-return"
            {...{ onChange, value }}
            hasErrors={!!errors?.estimatedReturn}
            minDate={new Date()}
            maxDate={maxDate}
            showTimeSelect
            isClearable
            timeIntervals={30}
            dateFormat="MM/dd/yyyy 'at' h:mm aa"
            placeholderText="Select a date and time"
            onFocus={(event) => (event.target.readOnly = true)}
          />
        )}
      />
      {errors?.estimatedReturn && (
        <Form.Error>{errors.estimatedReturn.message}</Form.Error>
      )}
    </div>
  )

  const renderModalBody = () => (
    <Modal.Body>
      {createMutation.isError && (
        <div className="mb-2 flex w-full">
          <Form.Error>{createMutation.error.message}</Form.Error>
        </div>
      )}
      <div className="mb-4 grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <Form.Label htmlFor="schedule-appointment-reservation">
            Reservation
          </Form.Label>
          {launchableReservations?.length > 0 ? (
            renderReservationSelect()
          ) : (
            <Form.Error>
              You do not have any current or upcoming reservations at a marina
              that offers dry stack launches
            </Form.Error>
          )}
          {errors?.reservation && (
            <Form.Error>{errors.reservation.message}</Form.Error>
          )}
        </div>
        {selectedReservation && (
          <>
            {renderDateInput()}
            {renderAvailabilityInputs()}
            {renderExceptionNote()}
          </>
        )}
        {selectedReservation && addOnCatalogError && (
          <div className="col-span-12">
            <AlertBanner type="error">
              Couldn&apos;t load add-ons for this launch. Refresh and try again,
              or continue without them.
            </AlertBanner>
          </div>
        )}
        {selectedReservation &&
          !addOnCatalogError &&
          addOnCatalogItems.length > 0 && (
            <div className="col-span-12" ref={addOnsSectionRef}>
              <Form.Label>Add-Ons</Form.Label>
              <Form.Subtext>
                Have anything ready when you get here?
              </Form.Subtext>
              <Controller
                name="addOns"
                control={control}
                rules={{
                  validate: (value) =>
                    !hasIncompleteAddOnSelection(addOnCatalogItems, value) ||
                    "Complete or remove the highlighted add-on to continue.",
                }}
                render={({ field }) => (
                  <LaunchAddOnFields
                    catalog={addOnCatalogItems}
                    value={field.value}
                    onChange={field.onChange}
                    estimate={addOnEstimate.estimate}
                    estimateFailed={addOnEstimate.failed}
                    validationAttempted={Boolean(errors.addOns)}
                  />
                )}
              />
              {errors?.addOns && (
                <Form.Error>{errors.addOns.message}</Form.Error>
              )}
            </div>
          )}
        {renderNoteInput()}
        {renderEstimatedReturnInput()}
      </div>
    </Modal.Body>
  )

  const renderModalFooter = () => (
    <Modal.Footer
      secondaryOnSubmit={handleClose}
      secondaryBtnDisabled={fetchingAvailability || createMutation.isLoading}
      secondaryBtnText="Cancel"
      secondaryBtnVariant="tertiary"
      confirmBtnType="submit"
      confirmBtnText={createMutation.isLoading ? "Submitting" : "Submit"}
      disabled={
        hasNoAvailability() ||
        fetchingAvailability ||
        !selectedReservation ||
        createMutation.isLoading
      }
      confirmBtnLoading={createMutation.isLoading}
    />
  )

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <Modal.Header title="Schedule New Launch" />
      <div className="overflow-y-auto">
        <Form onSubmit={handleSubmit(handleFormSubmit, handleInvalidSubmit)}>
          {renderModalBody()}
          {renderModalFooter()}
        </Form>
      </div>
    </Modal>
  )
}

ScheduleLaunchModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  launchableReservations: PropTypes.arrayOf(PropTypes.object),
  isOpen: PropTypes.bool.isRequired,
}

export default ScheduleLaunchModal
